import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import {
  BLACKLISTED_ROLES,
  NO_OPERATIONAL_USER_APPLICATIONS,
  REQUIRED_APPLICATIONS,
  TENANT_LEVEL_APPLICATIONS
} from '~/constants/_core/application-launcher';
import { SAFEA_V3_ENABLED, SAFEA_VERSION, SAFEA_VERSIONS } from '~/constants/_core/safea-v3';

/**
 * SCENARIOS
 * v2 to v2 => sso endpoint
 * v2 to migrating => sso endpoint + v3 token exchange
 * v2 to v3 => sso endpoint + v3 token exchange
 *
 * migrating to v2 => sso endpoint
 * migrating to migrating => simple redirect
 * migrating to v3 => simple redirect
 *
 * v3 to v2 => impossible
 * v3 to migrating => simple redirect
 * v3 to v3 => simple redirect
 */

function isLegacySSO(application) {
  return application.safeaVersion === SAFEA_VERSIONS.V2 || !SAFEA_V3_ENABLED;
}

function isV3Compatible(application) {
  return application.safeaVersion === SAFEA_VERSIONS.MIGRATING || application.safeaVersion === SAFEA_VERSIONS.V3;
}

function validateApplications(applications) {
  const missingLauncherData = applications.filter(
    application => application.allowAppLauncher && (!application.appLauncherCommercialName || !application.appLauncherLogo)
  );
  if (missingLauncherData.length > 0) {
    console.warn('[Cockpit] [App launcher] The following applications are missing commercial names or logos:', missingLauncherData);
  }
}

function hasRoleForThisOwner(application, owner, currentTenantUuid = null) {
  if (owner === 'ies') {
    // If we are going to customer-center, the URL will be upgraded to tenant anyway
    if (TENANT_LEVEL_APPLICATIONS.includes(application.alias)) {
      return application.tenants.length > 0;
    }

    return application.tenants.some(tenant => tenant.institutions.length > 0);
  }

  if (owner === 'tenant') {
    return application.tenants.length > 0;
  }

  if (currentTenantUuid) {
    return application.tenants.some(t => t.uuid === currentTenantUuid);
  }
  return application.roles.length > 0
  || application.tenants.some(t => t.roles.length > 0)
  || application?.institutions?.some(i => i.roles.length > 0);
}

/**
 * Checks if the user can access the target role
 * The *target role* is the same as the current role, but with a different application
 *
 * If we are going to a TENANT_LEVEL_APPLICATIONS (customer-center),
 * it might "upgrade" the role from institution to tenant
 *
 * This only executes for v3 SSO
 * In v2, this logic is handled by the SSO endpoint
 *
 * @param application
 * @param targetRole
 * @returns {{
 *   sameLevel: boolean,
 *   sameRole: boolean
 * }}
 */
function canAccessTargetRole(application, targetRole) {
  const { institutionUuid, tenantUuid, v2RoleId } = targetRole;
  if (tenantUuid) {
    const tenant = application.tenants.find(t => t.uuid === tenantUuid);
    if (!tenant) {
      return {
        sameLevel : false,
        sameRole  : false
      };
    }

    if (institutionUuid) {
      const institution = tenant.institutions.find(i => i.uuid === institutionUuid);
      if (SAFEA_V3_ENABLED) {
        return {
          sameLevel : true,
          sameRole  : (institution?.roles ?? []).length > 0
        };
      }
      return {
        sameLevel : !!institution,
        sameRole  : (institution?.roles ?? []).some(role => role.id === v2RoleId)
      };
    }

    if (SAFEA_V3_ENABLED) {
      return {
        sameLevel : true,
        sameRole  : tenant.roles.length > 0
      };
    }
    return {
      sameLevel : true,
      sameRole  : tenant.roles.some(role => role.id === v2RoleId)
    };
  }

  if (SAFEA_V3_ENABLED) {
    return {
      sameLevel : true,
      sameRole  : application.roles.length > 0
    };
  }
  return {
    sameLevel : true,
    sameRole  : application.roles.some(role => role.id === v2RoleId)
  };
}

function filterAvailableApplications(applications, context) {
  const { applicationAlias, isOperation, owner, targetRole } = context;
  return applications.filter(application => {
    if (application.alias === applicationAlias || !application.allowAppLauncher) {
      return false;
    }

    // All v3 applications need an appLauncherSsoUrl
    // If we are in v2, it's not needed because the SSO endpoint will return it
    if (SAFEA_V3_ENABLED && isV3Compatible(application) && !application.appLauncherSsoUrl) {
      return false;
    }

    // We cannot SSO from a v3 application to v2 (incompatible tokens)
    if (SAFEA_VERSION === SAFEA_VERSIONS.V3 && application.safeaVersion === SAFEA_VERSIONS.V2) {
      return false;
    }

    // We are on v2, but going to v3 - we need access on the same level (but different role)
    if (
      SAFEA_VERSION === SAFEA_VERSIONS.V2
      && isV3Compatible(application)
      && !TENANT_LEVEL_APPLICATIONS.includes(application.alias)
      && !canAccessTargetRole(application, targetRole).sameLevel
    ) {
      return false;
    }

    // These applications don't support operational users =/
    if (isOperation && NO_OPERATIONAL_USER_APPLICATIONS.includes(application.alias)) {
      return false;
    }

    return hasRoleForThisOwner(
      application,
      owner,
      targetRole.tenantUuid
    );
  }).sort((a, b) => a.appLauncherCommercialName.localeCompare(b.appLauncherCommercialName));
}


function createRoleResult(role, tenant = null, institution = null, safeaVersion) {
  return {
    v2: {
      roleAlias   : role.alias,
      tenantAlias : tenant?.alias || null,
      iesAlias    : institution?.alias || null
    },
    v3: {
      v2RoleId        : role.id,
      tenantUuid      : tenant?.uuid || null,
      institutionUuid : institution?.uuid || null
    }
  }[safeaVersion];
}

/**
 * Finds the first available role for the user
 * The priority is the role with the highest level of privilege
 *
 * This only executes for v3 SSO
 * In v2, we can only access applications in the same level as the current role
 *
 * @param {object} application
 * @param {object} targetRole
 * @param {string} safeaVersion
 * @returns {
 *   { roleAlias: string|null, tenantAlias: string|null, iesAlias: string|null }
 *   | { v2RoleId: number|null, tenantUuid: string|null, institutionUuid: string|null }
 * }
 */
function findFirstAvailableRole(application, targetRole, safeaVersion) {
  const isTenantLevelApplication = TENANT_LEVEL_APPLICATIONS.includes(application.alias);
  const isTenantLevelRole = (targetRole.tenantUuid !== null && targetRole.institutionUuid === null);

  if (
    canAccessTargetRole(application, targetRole).sameRole &&
    (!isTenantLevelApplication || isTenantLevelRole)
  ) {
    return targetRole;
  }

  const { tenantUuid, institutionUuid } = targetRole;

  if (tenantUuid) {
    const tenant = application.tenants.find(t => t.uuid === tenantUuid);

    if (tenant) {
      if (institutionUuid) {
        const institution = tenant?.institutions?.find(i => i.uuid === institutionUuid && i.roles.length > 0);
        if (institution) {
          return createRoleResult(institution.roles[0], tenant, institution, safeaVersion);
        }
      }

      if (tenant.roles.length > 0) {
        return createRoleResult(tenant.roles[0], tenant, null, safeaVersion);
      }

      const institution = tenant?.institutions?.find(i => i.roles.length > 0);
      if (institution) {
        return createRoleResult(institution.roles[0], tenant, institution, safeaVersion);
      }
    }
  }

  if (application.roles.length > 0) {
    return createRoleResult(application.roles[0], null, null, safeaVersion);
  }

  const institution = application?.institutions?.find(i => i.roles.length > 0);
  if (institution) {
    return createRoleResult(institution.roles[0], null, institution, safeaVersion);
  }

  const tenant = application?.tenants?.find(t => t.roles.length > 0);
  if (tenant) {
    return createRoleResult(tenant.roles[0], tenant, null, safeaVersion);
  }

  throw new Error('[Cockpit] [App launcher] No role available for the current user');
}

export default Vue.extend({
  name: 'ApplicationLauncher',
  data() {
    return {
      contexts: []
    };
  },
  computed: {
    ...mapGetters({
      isImpersonated: '_core/auth/isImpersonated'
    }),
    ...mapState({
      tenant                : (state) => state._core.tenant,
      applicationAlias      : (state) => state._core.application.alias,
      currentContext        : (state) => state._core.user.currentContext,
      currentRole           : (state) => state._core.auth.currentRole,
      availableApplications : (state) => state._core.user.availableApplications,
      isOperation           : (state) => state._core.application.isOperation,
      owner                 : (state) => state._core.application.owner
    }),
    applications() {
      // On v2, we build the same format as v3
      // Ugly but works, this will be nuked with alongside v2
      if (!SAFEA_V3_ENABLED) {
        const applicationsV2 = {};
        const { tenants, applications } = this.availableApplications;

        for (const application of applications) {
          if (!applicationsV2[application.id]) {
            applicationsV2[application.id] = {
              ...application,
              roles   : [],
              tenants : []
            };
          }

          applicationsV2[application.id].roles.push(...application.roles);
        }

        for (const tenant of tenants) {
          for (const application of tenant.applications) {
            if (!applicationsV2[application.id]) {
              applicationsV2[application.id] = {
                ...application,
                roles   : [],
                tenants : []
              };
            }

            let formattedTenant = applicationsV2[application.id].tenants.find(t => t.id === tenant.id);
            if (!formattedTenant) {
              formattedTenant = {
                ...tenant,
                roles        : [],
                institutions : []
              };
              applicationsV2[application.id].tenants.push(formattedTenant);
            }

            formattedTenant.roles.push(...application.roles);
          }

          for (const institution of tenant.ies) {
            for (const institutionApplication of institution.applications) {
              if (!applicationsV2[institutionApplication.id]) {
                applicationsV2[institutionApplication.id] = {
                  ...institutionApplication,
                  roles   : [],
                  tenants : []
                };
              }

              let tenantFromInstitution = applicationsV2[institutionApplication.id].tenants.find(t => t.id === tenant.id);
              if (!tenantFromInstitution) {
                tenantFromInstitution = {
                  ...tenant,
                  roles        : [],
                  institutions : []
                };
                applicationsV2[institutionApplication.id].tenants.push(tenantFromInstitution);
              }

              let formattedInstitution = tenantFromInstitution.institutions.find(i => i.id === institution.id);
              if (!formattedInstitution) {
                formattedInstitution = {
                  ...institution,
                  roles: []
                };
                tenantFromInstitution.institutions.push(formattedInstitution);
              }

              formattedInstitution.roles.push(...institutionApplication.roles);
            }
          }
        }

        return Object.values(applicationsV2);
      }

      // v3 case, we trust the backend
      return this.contexts;
    },
    applicationsToDisplay() {
      validateApplications(this.applications);
      return filterAvailableApplications(this.applications, {
        applicationAlias : this.applicationAlias,
        isOperation      : this.isOperation,
        owner            : this.owner,
        targetRole       : {
          tenantUuid      : this.currentContext?.tenant?.uuid ?? this.currentRole?.tenant?.uuid,
          institutionUuid : this.currentContext?.institution?.uuid ?? this.currentRole?.ies?.uuid,
          v2RoleId        : this.currentRole?.role?.roleId ?? null
        }
      });
    },
    isVisible() {
      // The user must have access to all required applications
      const hasRequiredApplications = REQUIRED_APPLICATIONS.every(alias => {
        return this.applications.some(app => app.alias === alias);
      });

      const roles = [];
      for (const application of this.applications) {
        roles.push(...application.roles);

        for (const tenant of application.tenants) {
          roles.push(...tenant.roles);
          for (const institution of tenant.institutions) {
            roles.push(...institution.roles);
          }
        }
      }

      // The user needs at least one non-blacklisted role in *any application* to see the launcher
      const hasAtLeastOneAdministrativeRole = roles.some(role => !BLACKLISTED_ROLES.includes(role.alias));
      return (hasRequiredApplications && hasAtLeastOneAdministrativeRole) && this.applicationsToDisplay.length > 0;
    }
  },
  async mounted() {
    if (SAFEA_V3_ENABLED) {
      const { contexts } = await this.$services._core.userV3().getAllContexts();
      this.contexts = contexts;
    }
  },
  methods: {
    async executeV2SSO(application) {
      if (!isLegacySSO(application)) {
        return;
      }

      const targetRole = {
        v2RoleId        : this.currentRole?.role?.id,
        tenantUuid      : this.currentRole?.tenant?.uuid,
        institutionUuid : this.currentRole?.ies?.uuid
      };

      let v2Role = {
        roleAlias   : this.currentRole?.role?.alias,
        tenantAlias : this.currentRole?.tenant?.alias,
        iesAlias    : this.currentRole?.ies?.alias
      };
      if (!canAccessTargetRole(application, targetRole).sameRole) {
        v2Role = findFirstAvailableRole(application, targetRole, SAFEA_VERSIONS.V2);
      }

      const payload = TENANT_LEVEL_APPLICATIONS.includes(this.applicationAlias)
        // If we leaving customer-center, don't send the role
        ? {}
        : v2Role;

      this.$nuxt.$loading.start();
      try {
        const { data } = await this.$services._core.auth().signinSSOUrl(payload, application.alias, {
          isAppLauncher: true
        });
        const { appLauncherRedirectUrl } = data;

        let hostname = window.location.hostname === 'localhost' ? process.env.DEV_APPLICATION_HOST : window.location.hostname;
        if (!this.isOperation && this.currentRole && this.currentRole.ies && this.currentRole.ies?.uuid && TENANT_LEVEL_APPLICATIONS.includes(application.alias)) {
          hostname = this.tenant.hostname || (this.currentRole.tenant && this.currentRole.tenant.hostname);
        }

        window.open(appLauncherRedirectUrl.replace('#hostname#', hostname), '_blank');
      } catch (err) {
        console.error(err);
        this.$toast.error(err.message);
      } finally {
        this.$nuxt.$loading.finish();
      }
    },
    getRedirectUrl(application) {
      // We cannot build a URL on v2, the SSO endpoint must be called first
      if (isLegacySSO(application)) {
        return null;
      }

      // migrating or v3

      // If we are leaving a tenant only application (i.e. customer-center), we do not send the access level
      // That way the user doesn't lose the role he was previously using
      const targetRole = {
        v2RoleId        : this.currentRole?.role?.roleId,
        tenantUuid      : this.currentContext?.tenant?.uuid,
        institutionUuid : this.currentContext?.institution?.uuid
      };

      const availableRole = TENANT_LEVEL_APPLICATIONS.includes(this.applicationAlias)
        ? {}
        : findFirstAvailableRole(application, targetRole, SAFEA_VERSIONS.V3);

      let hostname = window.location.hostname === 'localhost' ? process.env.DEV_APPLICATION_HOST : window.location.hostname;

      // For some applications, we need to change the hostname to the tenant one.
      // Only switch hostnames if we are at the institution level, and we aren't an operational user
      if (!this.isOperation && !!this.currentContext.institution?.uuid && TENANT_LEVEL_APPLICATIONS.includes(application.alias)) {
        hostname = this.tenant.hostname;
      }

      const destinationHostname = application.appLauncherSsoUrl.replace('#hostname#', hostname);
      let url;
      try {
        url = new URL(destinationHostname);
      } catch (err) {
        // Someone fat-fingered the URL
        console.warn('Invalid URL for app launcher', destinationHostname);
        return null;
      }
      for (const [key, value] of Object.entries(availableRole)) {
        url.searchParams.append(key, value || '');
      }

      return url.toString();
    }
  }
});
